
import { useEffect, useRef } from 'react';
import { setDocumentTitle, getDocumentTitle } from '../utils/documentUtils';
import { useTranslation } from 'react-i18next';

/**
 * React hook for managing document title with safety measures
 * @param title The title to set
 * @param dependencies Optional array of dependencies to trigger title updates
 */
export const useDocumentTitle = (
  title: string,
  dependencies: React.DependencyList = []
): void => {
  // Store the previous title to restore it when the component unmounts
  const previousTitle = useRef<string>(getDocumentTitle());

  useEffect(() => {
    // Set the document title when component mounts or dependencies change
    if (title) {
      setDocumentTitle(title);
    }
    
    // Restore the previous title when component unmounts
    return () => {
      if (previousTitle.current) {
        setDocumentTitle(previousTitle.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, ...dependencies]);
};

/**
 * React hook for managing document title with i18n integration
 * @param titleKey The i18n key for the title
 * @param options Optional i18n options
 * @param dependencies Optional array of dependencies to trigger title updates
 */
export const useTranslatedDocumentTitle = (
  titleKey: string,
  options?: Record<string, any>,
  dependencies: React.DependencyList = []
): void => {
  const { t } = useTranslation();
  
  useEffect(() => {
    // Set the document title using the translation
    if (titleKey) {
      try {
        // Ensure we always get a string from translation
        const translatedTitle = t(titleKey, options || {});
        // Make sure we have a string before setting the title
        if (typeof translatedTitle === 'string') {
          setDocumentTitle(translatedTitle);
        } else {
          // If translation returns a non-string (object, etc.), use a default or the key itself
          setDocumentTitle(`Communitly Platform - ${titleKey}`);
          console.warn('Translation did not return a string for title:', titleKey);
        }
      } catch (error) {
        console.error('Error translating title:', error);
        // Set a fallback title on error
        setDocumentTitle('Communitly Platform');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titleKey, t, ...(options ? [options] : []), ...dependencies]);
};

export default useDocumentTitle;
